// Phase 2.1 — the vocabulary translation behind SeverityBadge / StatusBadge.
//
// Kept apart from the component because it is the only part with real logic:
// this backend spells the same rank six ways ("Critical", "critical", "sev1",
// "P2", "informational"…) and the same workflow state a dozen ways
// ("FalsePositive", "false positive", "wont_fix"). Everything downstream —
// colour, shape, emphasis — is decided in CSS from the tone name, so this map
// is the single place a new API spelling has to be taught.
//
// Runnable check: `node --experimental-strip-types src/lib/tone.check.ts`

export type Tone = "critical" | "high" | "medium" | "low" | "info" | "none";
export type StateTone = "new" | "investigating" | "contained" | "resolved" | "closed";

const SEVERITY_ALIASES: Record<string, Tone> = {
  critical: "critical", crit: "critical", sev0: "critical", p1: "critical",
  high: "high", sev1: "high", p2: "high",
  medium: "medium", med: "medium", moderate: "medium", sev2: "medium", p3: "medium",
  low: "low", sev3: "low", p4: "low",
  info: "info", informational: "info", information: "info",
  none: "none", unknown: "none",
  // Syslog levels, which audit trails report severity in (altide-d2d-uem
  // audit log: critical / warning / notice / info).
  warning: "medium", warn: "medium", notice: "info",
};

/** Maps whatever the API calls it onto one of the five ranks. */
export function toSeverityTone(value: string | null | undefined): Tone {
  return SEVERITY_ALIASES[(value ?? "").trim().toLowerCase()] ?? "none";
}

const STATE_ALIASES: Record<string, StateTone> = {
  new: "new", open: "new", reopened: "new", active: "new",
  investigating: "investigating", "in progress": "investigating", in_progress: "investigating",
  assigned: "investigating", triage: "investigating",
  contained: "contained", mitigated: "contained", containment: "contained",
  resolved: "resolved", remediated: "resolved", done: "resolved", patched: "resolved",
  closed: "closed", falsepositive: "closed", "false positive": "closed",
  false_positive: "closed", suppressed: "closed", ignored: "closed", wont_fix: "closed",

  // Endpoint management (altide-d2d-uem, cascade plan 5.2). The five hues
  // mean the same here as for an incident: new = waiting to start,
  // investigating = under way, contained = held on purpose, resolved = done
  // and healthy, closed = inactive or ended. Nothing below may be red or
  // orange; an outcome that IS a risk goes through toOutcomeTone instead.
  pending: "new", queued: "new", requested: "new", scheduled: "new", sent: "new",
  pending_approval: "new", awaiting_approval: "new", pending_execution: "new",
  running: "investigating", enrolling: "investigating", provisioning: "investigating",
  dispatched: "investigating", delivered: "investigating", installing: "investigating",
  paused: "contained", deferred: "contained", notnow: "contained", not_now: "contained",
  pending_reboot: "contained", exempt: "contained", maintenance: "contained",
  online: "resolved", compliant: "resolved", succeeded: "resolved", success: "resolved",
  completed: "resolved", acknowledged: "resolved", applied: "resolved", approved: "resolved",
  installed: "resolved", enrolled: "resolved", already_enrolled: "resolved", verified: "resolved",
  offline: "closed", unenrolled: "closed", cancelled: "closed", canceled: "closed",
  disabled: "closed", deleted: "closed", not_evaluated: "closed", inactive: "closed",
  idle: "closed", rejected: "closed", revoked: "closed", expired: "closed", skipped: "closed",
  enforcing: "investigating", published: "resolved", draft: "closed", retired: "closed",
  unsigned: "closed", accepted: "closed",

  // Zero trust access (d2d-ZTA, cascade plan 5.1). Same five meanings.
  // Already above and shared: pending (new), approved (resolved), expired and
  // revoked (closed), active (new), idle (closed). A live session is "active",
  // and keeps active's meaning here: open, not a risk.
  terminated: "closed", recording: "investigating",
  healthy: "resolved", degraded: "contained", unhealthy: "contained", unknown: "closed",
  valid: "resolved", expiring: "contained",
  connected: "resolved", disconnected: "closed",

  // Exposure and vulnerability management (d2d-DetectHub, cascade plan 5.1).
  // Same five meanings. Already above and shared: open (new), in_progress
  // (investigating), false_positive / suppressed / ignored (closed), queued /
  // pending (new), running (investigating), completed (resolved), cancelled /
  // skipped / rejected / expired / revoked / disabled (closed), approved
  // (resolved), active (new). A risk accepted on purpose is held, not closed.
  fixed: "resolved", risk_accepted: "contained",
  scanning: "investigating", syncing: "investigating", patching: "investigating", verifying: "investigating",
  stopped: "closed", blocked: "contained",
  validated: "resolved", invalid: "contained", enabled: "resolved",
  pending_review: "new", archived: "closed", suspended: "contained", no_change: "closed",
  pass: "resolved", passed: "resolved", not_applicable: "closed",
  refuted: "resolved", inconclusive: "contained",
  approval_required: "new", cancelling: "investigating",
  listen: "resolved", listening: "resolved", configured: "resolved",
  document_pending: "new", needs_review: "new", document_submitted: "investigating",
  // A compliance test that warns: attention short of a failure.
  warning: "contained",
};

/**
 * Maps a backend status string onto one of the five workflow states.
 * Unknown values fall back to "closed" — the neutral, unalarming tone — so a
 * status this app has never seen renders as a grey chip rather than borrowing
 * a colour that would imply a risk level it does not have.
 */
export function toStateTone(value: string | null | undefined): StateTone {
  return STATE_ALIASES[(value ?? "").trim().toLowerCase()] ?? "closed";
}

export type OutcomeTone = Tone | StateTone;

// Outcomes that are themselves a risk take the severity ramp, not a status
// hue: a failed deployment or a non-compliant device is something to act on,
// with a rank. The list is short and explicit on purpose. Anything not on it
// is a workflow state and resolves exactly as toStateTone does.
const RISK_OUTCOMES: Record<string, Tone> = {
  failed: "high", failure: "high", error: "high", commandformaterror: "high",
  timeout: "high", timed_out: "high",
  non_compliant: "high", noncompliant: "high", "non-compliant": "high", "non compliant": "high",
  at_risk: "medium", missing: "medium", rolled_back: "medium", denied: "medium",
  lost: "high",
  // d2d-ZTA (cascade plan 5.1): an expired certificate still presented is a
  // risk, where an expired access request has simply ended (closed, above).
  // Callers name the certificate case explicitly rather than overloading
  // "expired".
  certificate_expired: "high",
  // d2d-DetectHub (cascade plan 5.1): a proven exploit is the worst finding
  // there is; a compliance control that fails, or only partly passes, is a gap
  // to act on. Their workflow twins (refuted, inconclusive, pass) are states.
  proven: "critical",
  fail: "high", partial: "medium",
  // A scan that finished but could not cover every target leaves a gap.
  completed_with_gaps: "medium",
};

/**
 * A status that may be an outcome. Risk outcomes (failed, non-compliant, a
 * missing patch) map to a severity tone; everything else is toStateTone.
 */
export function toOutcomeTone(value: string | null | undefined): OutcomeTone {
  return RISK_OUTCOMES[(value ?? "").trim().toLowerCase()] ?? toStateTone(value);
}
